import React, { FC, useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Chip } from '@material-ui/core'
import ChipInput from 'material-ui-chip-input'
import KeywordChipList from './KeywordChipList'
import { hiraToKana, kanaToHira } from '../../../utils/string'
import { ReactSVG } from 'react-svg'
import { useTranslation } from 'react-i18next'

interface Props {
  placeHolder: string
  efficiency: {
    modalName: string
    options: string[] | undefined
    keywords: string[]
    selectLimit: number
    displayLimit: number
    setKeywords: (keyword: string[]) => void
  }
  stress: {
    modalName: string
    options: string[] | undefined
    keywords: string[]
    selectLimit: number
    displayLimit: number
    setKeywords: (keyword: string[]) => void
  }
  focus: {
    modalName: string
    options: string[] | undefined
    keywords: string[]
    selectLimit: number
    displayLimit: number
    setKeywords: (keyword: string[]) => void
  }
  resetKeywords: () => void
}

interface Keyword {
  type: string
  word: string
  value: string
  checked: boolean
  displayed: boolean
}

const ScoreKeywordInput: FC<Props> = (props) => {
  const {
    placeHolder,
    stress,
    efficiency,
    focus,
    resetKeywords,
  } = props

  const { t } = useTranslation()

  const [keywordList, setKeywordList] = useState<Keyword[]>([])
  const [keywordErrorMessage, setKeywordErrorMessage] = useState('')
  const [isKeywordDisplay, setIsKeywordDisplay] = useState(false)
  const [keywordHintMessage, setKeywordHintMessage] = useState('')
  const [tmpKeyword, setTmpKeyword] = useState<string>('')

  const [selectedKeywordList, setSelectedKeywordList] = useState<string[]>([])

  const synchronizeKeywordListChip = (keyword: string | number, deleteMode = false, clearMode = false, type?: string) => {
    const updateList = keywordList.map((value) => {
      if (type) {
        if (value.type === type && value.value === keyword) {
          return { ...value, checked: !deleteMode }
        }
        if (clearMode) {
          return { ...value, displayed: true }
        }
        return value
      }
      if (value.value === keyword) {
        return { ...value, checked: !deleteMode }
      }
      if (clearMode) {
        return { ...value, displayed: true }
      }
      return value
    })
    setKeywordList(updateList)
  }

  const addEfficiencyKeyword = (keyword: string, clearMode = false) => {
    setKeywordErrorMessage('')
    efficiency.setKeywords([...efficiency.keywords, keyword])
    synchronizeKeywordListChip(keyword, false, clearMode, "efficiency")
    setTmpKeyword('')
  }

  const addStressKeyword = (keyword: string, clearMode = false) => {
    setKeywordErrorMessage('')
    stress.setKeywords([...stress.keywords, keyword])
    synchronizeKeywordListChip(keyword, false, clearMode, "stress")
    setTmpKeyword('')
  }

  const addFocusKeyword = (keyword: string, clearMode = false) => {
    setKeywordErrorMessage('')
    focus.setKeywords([...focus.keywords, keyword])
    synchronizeKeywordListChip(keyword, false, clearMode, "focus")
    setTmpKeyword('')
  }

  const resetKeywordList = () => {
    const updateList = keywordList.map((value) => (
      { ...value, checked: selectedKeywordList.includes(value.word), displayed: true }))
    setKeywordList(updateList)
  }

  const resetKeywordChip = () => {
    const updateList = keywordList.map((value) => ({ ...value, checked: false }))
    setKeywordList(updateList)
  }

  const closeKeywordModal = () => {
    setTmpKeyword('')
    setIsKeywordDisplay(false)
    resetKeywordList()
  }

  const deleteKeyword = (keyword: string) => {
    if (efficiency.keywords.includes(keyword)) {
      efficiency.setKeywords(efficiency.keywords.filter((val) => val !== keyword))
    } else if (stress.keywords.includes(keyword)) {
      stress.setKeywords(stress.keywords.filter((val) => val !== keyword))
    } else if (focus.keywords.includes(keyword)) {
      focus.setKeywords(focus.keywords.filter((val) => val !== keyword))
    }
    synchronizeKeywordListChip(keyword, true)
    setKeywordHintMessage('')
  }

  const keywordSelect = (word: string) => {
    let count = 0
    const lowerCasedWord = word.toLowerCase()
    const hiragana = kanaToHira(word)
    const katakana = hiraToKana(word)
    const updateList = keywordList.map((item) => {
      if (
        (
          (item.word.toLowerCase().indexOf(lowerCasedWord) > -1 || item.word.indexOf(hiragana) > -1 || item.word.indexOf(katakana) > -1)
        )
      ) {
        count += 1
        return { ...item, displayed: true }
      }
      return { ...item, displayed: false }
    })
    setKeywordList(updateList)
    setTmpKeyword(word)
  }

  const keywordSuggest = (e: React.FormEvent<HTMLInputElement>) => {
    const inputValue = e.currentTarget.value.trim()
    if (inputValue === "") {
      resetKeywordList()
      setTmpKeyword('')
      return
    }
    setIsKeywordDisplay(true)
    keywordSelect(inputValue)
  }

  const openKeywordModal = () => {
    setIsKeywordDisplay(true)
    resetKeywordList()
  }

  const resetKeyword = () => {
    resetKeywords()
    resetKeywordChip()
    setKeywordHintMessage('')
  }

  const choiceFirstChip = (keyCode: number) => {
    const tmpKeywordList = keywordList.filter((keyword) => keyword.displayed && !keyword.checked)
    if (keyCode !== 13 || !tmpKeywordList.length || !tmpKeyword) return
    if (tmpKeywordList[0].type === "efficiency") {
      addEfficiencyKeyword(tmpKeywordList[0].word, true)
    }
    if (tmpKeywordList[0].type === "stress") {
      addStressKeyword(tmpKeywordList[0].word, true)
    }
    if (tmpKeywordList[0].type === "focus") {
      addFocusKeyword(tmpKeywordList[0].word, true)
    }
  }

  useEffect(() => {
    setSelectedKeywordList([...efficiency.keywords, ...stress.keywords, ...focus.keywords])
  }, [efficiency.keywords, stress.keywords, focus.keywords])

  useEffect(() => {
    const efficiency_res = efficiency.options
      ? efficiency.options.map((option: string) => (
        { type: "efficiency", word: option, value: option, checked: false, displayed: true }
      ))
      : []
    const stress_res = stress.options
      ? stress.options.map((option: string) => (
        { type: "stress", word: option, value: option, checked: false, displayed: true }
      ))
      : []
    const focus_res = focus.options
      ? focus.options.map((option: string) => (
        { type: "focus", word: option, value: option, checked: false, displayed: true }
      ))
      : []
    const res = [...efficiency_res, ...stress_res, ...focus_res]
    setKeywordList(res)
  }, [])

  return (
    <div>
      <div className="input_field">
        <ReactSVG
          src="/static/images/icon/icon-search.svg"
          className="icon"
        />
        <ChipInput
          id="keywords"
          value={selectedKeywordList}
          placeholder={placeHolder}
          onUpdateInput={keywordSuggest}
          onClick={openKeywordModal}
          newChipKeyCodes={[]}
          newChipKeys={[""]}
          onFocus={openKeywordModal}
          onKeyDown={(key) => choiceFirstChip(key.keyCode)}
          inputValue={tmpKeyword}
          chipRenderer={({ value }, key) => (
            <Chip
              className="chip"
              key={key}
              label={value}
              variant="outlined"
              onDelete={() => deleteKeyword(value)}
              deleteIcon={<div>×</div>}
            />
          )}
        />
        {
          selectedKeywordList.length !== 0 && (
            <button type="button" className="reset" onClick={() => resetKeyword()}>
              ×
            </button>
          )
        }
      </div>
      <div>
        {
          isKeywordDisplay && (
            <>
              <div className="suggest_box" role="none" onClick={() => setTmpKeyword('')}>
                <div className="suggest_contents">
                  <KeywordChipList
                    modalName={efficiency.modalName}
                    keywordList={keywordList.filter((val) => val.type === "efficiency")}
                    addKeyword={addEfficiencyKeyword}
                  />
                  <KeywordChipList
                    modalName={stress.modalName}
                    keywordList={keywordList.filter((val) => val.type === "stress")}
                    addKeyword={addStressKeyword}
                  />
                  <KeywordChipList
                    modalName={focus.modalName}
                    keywordList={keywordList.filter((val) => val.type === "focus")}
                    addKeyword={addFocusKeyword}
                  />
                </div>
                <button type="button" className="closebox" onClick={closeKeywordModal}>
                  <FontAwesomeIcon icon={['far', 'times-circle']} />
                </button>
              </div>
              <div className="search_screen" role="presentation" onClick={closeKeywordModal} />
            </>
          )
        }
      </div>
      {
        keywordErrorMessage && (
          <div className="error_field">
            {keywordErrorMessage}
          </div>
        )
      }
      {
        keywordHintMessage && (
          <div className="hint_field">
            {keywordHintMessage}
          </div>
        )
      }
    </div>
  )
}

export default ScoreKeywordInput
